import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const STORAGE_KEY = 'spotify-playback-token';
const STORAGE_EXPIRY_KEY = 'spotify-playback-token-expires';
const STORAGE_REFRESH_KEY = 'spotify-playback-refresh-token';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Helper to get token from localStorage
function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEY);
}

// Helper to set token in localStorage
function setStoredToken(accessToken: string, expiresIn: number, refreshToken?: string) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, accessToken);
  localStorage.setItem(STORAGE_EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
  if (refreshToken) {
    localStorage.setItem(STORAGE_REFRESH_KEY, refreshToken);
  }
}

// Helper to clear tokens
function clearStoredTokens() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_EXPIRY_KEY);
  localStorage.removeItem(STORAGE_REFRESH_KEY);
}

// Subscribe to token refresh
function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// Notify all subscribers when token is refreshed
function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Refresh the access token
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_REFRESH_KEY) : null;

  if (!refreshToken) {
    console.error('No refresh token available');
    clearStoredTokens();
    return null;
  }

  try {
    const response = await axios.post('/api/spotify/refresh', {
      refreshToken,
    });

    if (response.data?.accessToken) {
      const { accessToken, expiresIn, refreshToken: newRefreshToken } = response.data;
      setStoredToken(accessToken, expiresIn, newRefreshToken);
      console.log('Spotify token refreshed successfully');
      return accessToken;
    }

    return null;
  } catch (error) {
    console.error('Failed to refresh Spotify token:', error);
    clearStoredTokens();
    return null;
  }
}

/**
 * Spotify API client with automatic token refresh
 */
export const spotifyApi = axios.create({
  baseURL: 'https://api.spotify.com/v1',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add Bearer token
spotifyApi.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 with automatic token refresh
spotifyApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, wait for the new token
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(spotifyApi(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Update the failed request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // Notify all waiting requests
          onTokenRefreshed(newToken);
          isRefreshing = false;

          // Retry the original request
          return spotifyApi(originalRequest);
        }
      } catch (refreshError) {
        isRefreshing = false;
        return Promise.reject(refreshError);
      }

      isRefreshing = false;
    }

    // For 204 No Content (valid response from some endpoints), don't treat as error
    if (error.response?.status === 204) {
      return Promise.resolve({ data: null, status: 204 } as any);
    }

    return Promise.reject(error);
  }
);

export default spotifyApi;
